import { Variants } from "framer-motion";

// ============================================================================
// Timing Constants
// ============================================================================

export const TIMING = {
  instant: 0.1,
  fast: 0.15,
  normal: 0.25,
  slow: 0.4,
  slower: 0.6,
  stagger: 0.05,
  staggerSlow: 0.08,
} as const;

export const EASING = {
  smooth: [0.4, 0, 0.2, 1],
  entrance: [0.22, 1, 0.36, 1],
  exit: [0.4, 0, 1, 1],
  bounce: [0.68, -0.55, 0.265, 1.55],
  spring: { type: "spring", stiffness: 400, damping: 30 },
  gentle: { type: "spring", stiffness: 300, damping: 35 },
} as const;

// ============================================================================
// Page & Container Variants
// ============================================================================

/** Fade in with upward motion - ideal for page content */
export const fadeInUp: Variants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

/** Simple fade - for subtle transitions */
export const fade: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

/** Container that staggers children animations */
export const staggerContainer: Variants = {
  animate: {
    transition: {
      staggerChildren: TIMING.stagger,
    },
  },
};

/** Faster stagger for lists with many items */
export const staggerFast: Variants = {
  animate: {
    transition: {
      staggerChildren: 0.03,
    },
  },
};

// ============================================================================
// List & Table Row Variants
// ============================================================================

/** Table row slide-in from left */
export const tableRow: Variants = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -10 },
};

/** List item fade and slide up */
export const listItem: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

/** Card item with scale */
export const cardItem: Variants = {
  initial: { opacity: 0, y: 10, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, scale: 0.98 },
};

// ============================================================================
// Interactive Variants
// ============================================================================

/** Hover lift effect for cards */
export const hoverLift = {
  whileHover: { y: -2, transition: { duration: TIMING.fast } },
  whileTap: { y: 0 },
};

/** Press scale for buttons */
export const pressScale = {
  whileTap: { scale: 0.98 },
};

/** Hover scale for icons/badges */
export const hoverScale = {
  whileHover: { scale: 1.05 },
  whileTap: { scale: 0.95 },
};

// ============================================================================
// Utility Functions
// ============================================================================

/** Get transition with index-based delay for staggered animations */
export function getStaggerDelay(index: number, baseDelay = 0) {
  return {
    transition: {
      delay: baseDelay + index * TIMING.stagger,
      duration: TIMING.normal,
      ease: EASING.smooth,
    },
  };
}

/** Standard transition for most animations */
export const standardTransition = {
  duration: TIMING.normal,
  ease: EASING.smooth,
};

// ============================================================================
// Homepage Section Variants
// ============================================================================

/** Section reveal animation - consistent entrance for homepage sections */
export const sectionReveal: Variants = {
  initial: { opacity: 0, y: 30 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: TIMING.slow, ease: EASING.smooth },
  },
};

/** Section stagger container - staggers children with slower timing */
export const sectionStagger: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: TIMING.staggerSlow,
      delayChildren: 0.1,
    },
  },
};

/** Card hover with lift and shadow - for chef, event, blog cards */
export const cardHover = {
  whileHover: {
    y: -6,
    transition: { duration: TIMING.fast, ease: EASING.smooth },
  },
  whileTap: { scale: 0.98 },
};

/** Enhanced card hover for feature cards */
export const cardHoverLarge = {
  whileHover: {
    y: -8,
    scale: 1.02,
    transition: { duration: TIMING.normal, ease: EASING.gentle },
  },
  whileTap: { scale: 0.98 },
};

/** Primary CTA press scale */
export const ctaPress = {
  whileTap: { scale: 0.97 },
};

/** Secondary button press scale */
export const buttonPress = {
  whileTap: { scale: 0.98 },
};

